import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowRight, FolderOpen, Package, Search } from 'lucide-react'
import { Row } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Input } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import { isCatalogBrowsePath, paths } from 'src/routes/paths'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import {
  useEmbeddedCatalogParamsOptional,
  useProductsCatalogSearchParams,
} from 'src/pages/Products/embeddedCatalogParams'
import { CATALOG_BROWSE_SEARCH_PARAM } from 'src/pages/Products/utils'
import {
  useCatalogCategoriesForAutocomplete,
  useCatalogProductSuggestions,
  useCategorySuggestions,
  type CategorySuggestion,
} from './hooks'
import type { ProductSuggestionRow } from './hooks'

type SelectableItem =
  | { type: 'category'; categoriesParam: string; category: CategorySuggestion }
  | { type: 'product'; id: number; product: ProductSuggestionRow }

export const ProductsSearchAutocomplete = ({
  draft,
  onDraftChange,
  onSubmit,
}: {
  draft: string
  onDraftChange: (value: string) => void
  onSubmit: () => void
}) => {
  const { t } = useLocalization()
  const location = useLocation()
  const navigate = useNavigate()
  const embedded = useEmbeddedCatalogParamsOptional()
  const [, setSearchParams] = useProductsCatalogSearchParams()

  const rootRef = useRef<HTMLDivElement>(null)
  const [panelDismissed, setPanelDismissed] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const draftTrimmed = draft.trim()
  const hasDraft = draftTrimmed.length > 0
  const showPanel = hasDraft && !panelDismissed
  const debouncedDraft = useDebounce(draftTrimmed, 300)

  const { data: categoryRows = [] } = useCatalogCategoriesForAutocomplete()
  const categoryMatches = useCategorySuggestions(draftTrimmed, categoryRows)

  const productQueryEnabled = showPanel && debouncedDraft.length >= 2

  const { data: productSuggestions = [], isPending: isLoadingProducts } =
    useCatalogProductSuggestions(debouncedDraft, productQueryEnabled)

  const goToCategories = useCallback(
    (categoriesValue: string) => {
      if (embedded) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set('categories', categoriesValue)
            next.delete(CATALOG_BROWSE_SEARCH_PARAM)
            next.delete('search')
            return next
          },
          { replace: true }
        )
      } else if (isCatalogBrowsePath(location.pathname)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set('categories', categoriesValue)
            next.delete(CATALOG_BROWSE_SEARCH_PARAM)
            next.delete('search')
            return next
          },
          { replace: true }
        )
      } else {
        navigate(
          `${paths.allProducts}?categories=${encodeURIComponent(categoriesValue)}`
        )
      }
    },
    [embedded, location.pathname, navigate, setSearchParams]
  )

  const labelCategory = useCallback(
    (title: string | null) => {
      if (!title?.trim()) return ''
      const parts = title.split(',').map((p) => p.trim())
      return t(parts[0] ?? '', parts[1] ?? '')
    },
    [t]
  )

  const labelProduct = useCallback(
    (item_name_translations: string | null, article_number: string | null) => {
      const name = t(item_name_translations ?? '')
      if (article_number && name) {
        return `${name} · ${article_number}`
      }
      return name || article_number || ''
    },
    [t]
  )

  const handlePickProduct = useCallback(
    (id: number) => {
      navigate(`${paths.product}/${id}`)
    },
    [navigate]
  )

  const productSectionDebouncing =
    draftTrimmed.length >= 2 && debouncedDraft !== draftTrimmed
  const showProductLoading = isLoadingProducts || productSectionDebouncing
  const showProductSection = draftTrimmed.length >= 2

  const selectableItems = useMemo((): SelectableItem[] => {
    const items: SelectableItem[] = []
    for (const c of categoryMatches) {
      items.push({
        type: 'category',
        categoriesParam: c.categoriesParam,
        category: c,
      })
    }
    if (showProductSection && !showProductLoading) {
      for (const p of productSuggestions) {
        items.push({ type: 'product', id: p.id, product: p })
      }
    }
    return items
  }, [
    categoryMatches,
    productSuggestions,
    showProductSection,
    showProductLoading,
  ])

  const empty =
    categoryMatches.length === 0 &&
    (draftTrimmed.length < 2 ||
      (debouncedDraft.length >= 2 &&
        !showProductLoading &&
        productSuggestions.length === 0))

  useEffect(() => {
    if (draftTrimmed.length === 0) {
      setPanelDismissed(false)
    }
  }, [draftTrimmed])

  useEffect(() => {
    setActiveIndex(-1)
  }, [draftTrimmed])

  useEffect(() => {
    setActiveIndex((i) => {
      if (selectableItems.length === 0) return -1
      if (i < 0) return -1
      return Math.min(i, selectableItems.length - 1)
    })
  }, [selectableItems])

  useEffect(() => {
    if (!showPanel) return

    const onPointerDown = (e: PointerEvent) => {
      const el = rootRef.current
      if (!el?.contains(e.target as Node)) {
        setPanelDismissed(true)
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [showPanel])

  useEffect(() => {
    if (activeIndex < 0) return
    const el = document.getElementById(`catalog-suggestion-${activeIndex}`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const applySelection = useCallback(
    (item: SelectableItem) => {
      if (item.type === 'category') {
        goToCategories(item.categoriesParam)
      } else {
        handlePickProduct(item.id)
      }
    },
    [goToCategories, handlePickProduct]
  )

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const n = selectableItems.length

      if (e.key === 'ArrowDown') {
        if (!showPanel || n === 0) return
        e.preventDefault()
        setActiveIndex((i) => (i < n - 1 ? i + 1 : i))
        return
      }

      if (e.key === 'ArrowUp') {
        if (!showPanel || n === 0) return
        e.preventDefault()
        setActiveIndex((i) => (i > -1 ? i - 1 : -1))
        return
      }

      if (e.key === 'Enter') {
        if (activeIndex >= 0) {
          const item = selectableItems[activeIndex]
          if (item) {
            e.preventDefault()
            applySelection(item)
            return
          }
        }
        onSubmit()
      }
    },
    [
      activeIndex,
      applySelection,
      onSubmit,
      selectableItems,
      showPanel,
    ]
  )

  const optionRowClass =
    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors'

  return (
    <Row className="w-full justify-center">
      <div
        ref={rootRef}
        className="relative w-full max-w-2xl"
        onMouseDown={(e) => {
          const el = e.target as HTMLElement
          if (el.closest('[data-catalog-suggestion]')) {
            e.preventDefault()
          }
        }}
      >
        <Input
          fullWidth
          value={draft}
          onChange={(e) => {
            setPanelDismissed(false)
            onDraftChange(e.target.value)
          }}
          onFocus={() => setPanelDismissed(false)}
          onKeyDown={onInputKeyDown}
          placeholder={t('Search products...', 'Sök produkter...')}
          startContent={
            <Search
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden
            />
          }
          endContent={
            <button
              type="button"
              onClick={onSubmit}
              className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
              aria-label={t('Search', 'Sök')}
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          }
          className={cn(
            'h-12 rounded-2xl border border-[hsl(var(--border-subtle))] bg-white text-sm shadow-soft',
            'placeholder:text-muted-foreground/80',
            'focus-visible:border-[hsl(var(--border-inner))] focus-visible:ring-2 focus-visible:ring-ring/20',
            'pl-10 pr-12'
          )}
          role="combobox"
          aria-expanded={showPanel}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `catalog-suggestion-${activeIndex}` : undefined
          }
          aria-controls={
            showPanel ? 'catalog-search-listbox' : undefined
          }
        />

        {showPanel && (
          <div
            data-catalog-suggestion
            id="catalog-search-listbox"
            role="listbox"
            aria-label={t('Search suggestions', 'Sökförslag')}
            className={cn(
              'absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-[var(--radius-card)] border border-[hsl(var(--border-subtle))] bg-popover text-popover-foreground shadow-soft',
              'max-h-[min(320px,50vh)]'
            )}
          >
            <div className="max-h-[min(300px,48vh)] overflow-y-auto overflow-x-hidden p-1">
              {categoryMatches.length > 0 && (
                <div className="overflow-hidden">
                  <div
                    className="px-2 py-1.5 text-xs font-medium text-muted-foreground"
                    id="catalog-group-categories"
                  >
                    {t('Categories', 'Kategorier')}
                  </div>
                  {categoryMatches.map((c, i) => (
                    <button
                      key={`cat-${c.id}`}
                      type="button"
                      id={`catalog-suggestion-${i}`}
                      role="option"
                      aria-selected={activeIndex === i}
                      tabIndex={-1}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => goToCategories(c.categoriesParam)}
                      className={cn(
                        optionRowClass,
                        activeIndex === i &&
                          'bg-accent text-accent-foreground'
                      )}
                    >
                      <FolderOpen className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{labelCategory(c.title)}</span>
                    </button>
                  ))}
                </div>
              )}

              {showProductSection && (
                <div className="overflow-hidden">
                  <div
                    className="px-2 py-1.5 text-xs font-medium text-muted-foreground"
                    id="catalog-group-products"
                  >
                    {t('Products', 'Produkter')}
                  </div>
                  {showProductLoading && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      {t('Loading...', 'Laddar...')}
                    </div>
                  )}
                  {!showProductLoading &&
                    productSuggestions.map((p, i) => {
                      const flatIndex = categoryMatches.length + i
                      return (
                        <button
                          key={`prod-${p.id}`}
                          type="button"
                          id={`catalog-suggestion-${flatIndex}`}
                          role="option"
                          aria-selected={activeIndex === flatIndex}
                          tabIndex={-1}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          onClick={() => handlePickProduct(p.id)}
                          className={cn(
                            optionRowClass,
                            activeIndex === flatIndex &&
                              'bg-accent text-accent-foreground'
                          )}
                        >
                          <Package className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">
                            {labelProduct(
                              p.item_name_translations,
                              p.article_number
                            )}
                          </span>
                        </button>
                      )
                    })}
                  {!showProductLoading &&
                    productSuggestions.length === 0 &&
                    debouncedDraft.length >= 2 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        {t('No matching products', 'Inga matchande produkter')}
                      </div>
                    )}
                </div>
              )}

              {empty && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t('No matches', 'Inga träffar')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Row>
  )
}
